import React, { useState, useEffect } from 'react';
import { theme } from '../styles/design-system';
import Card, { CardContent, CardHeader, CardTitle } from './ui/Card';
import Button from './ui/Button';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Shield,
  Star,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  BarChart3,
  Settings,
  Play,
  Square,
  RotateCcw,
  Zap
} from 'lucide-react';
import WorkflowExplanation from './WorkflowExplanation';

import { useAuth } from '../contexts/AuthContext';

const FnOTradingPage = () => {
  const { user } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [showWorkflow, setShowWorkflow] = useState(false);
  const [status, setStatus] = useState(null);
  const [signals, setSignals] = useState([]);
  const [positions, setPositions] = useState({});
  const [config, setConfig] = useState({
    api_key: '',
    access_token: '',
    user_id: user?.id?.toString() || '',
    numerology_api_url: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    poll_secs: 30,
    paper: true,
    futures_enabled: true,
    options_enabled: true,
    enable_muhurat_timing: true,
    enable_numerology_signals: true,
    capital: 100000,
    max_daily_loss: 1000,
    max_futures_positions: 2,
    max_options_positions: 3,
    min_compatibility_score: 0.15,
    signal_confidence_threshold: 0.35,
    enable_auto_square_off: true,
    square_off_time: '15:25',
    enable_greeks_analysis: true
  });

  // Update config when user loads
  useEffect(() => {
    if (user?.id) {
      setConfig(prev => ({ ...prev, user_id: user.id.toString() }));
    }
  }, [user]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [logs, setLogs] = useState([]);

  // API URL with fallback - define early
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    if (isRunning) {
      const interval = setInterval(() => {
        fetchStatus();
        fetchSignals();
        fetchPositions();
        fetchBackendLogs();
      }, 2000); // Poll every 2 seconds
      return () => clearInterval(interval);
    }
  }, [isRunning]);

  const fetchBackendLogs = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_URL}/algo/logs?limit=30`);
      if (response.ok) {
        const data = await response.json();
        if (data.logs && data.logs.length > 0) {
          setLogs(prevLogs => {
            const newLogs = data.logs.filter(newLog =>
              !prevLogs.some(prevLog =>
                prevLog.timestamp === newLog.timestamp && prevLog.message === newLog.message
              )
            ).map(log => ({
              message: log.message,
              level: log.level,
              source: log.source,
              timestamp: new Date(log.timestamp).toLocaleTimeString()
            }));

            if (newLogs.length === 0) return prevLogs;
            return [...prevLogs, ...newLogs].slice(-100);
          });
        }
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/algo/fno/status`);
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const fetchSignals = async () => {
    try {
      const response = await fetch(`${API_URL}/algo/fno/signals?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setSignals(data.signals || []);
      }
    } catch (error) {
      console.error('Error fetching signals:', error);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await fetch(`${API_URL}/algo/fno/positions`);
      if (response.ok) {
        const data = await response.json();
        setPositions(data);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
    }
  };



  const startAlgo = async () => {
    console.log('Starting F&O algo...');
    console.log('API_URL:', API_URL);
    console.log('Config:', config);

    setLoading(true);
    setError(null);

    try {
      const url = `${API_URL}/algo/start-fno`;
      console.log('Calling:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (response.ok) {
        setIsRunning(true);
        setError(null);
        console.log('✅ F&O algo started successfully');
      } else {
        const errorMsg = data.detail || JSON.stringify(data) || 'Failed to start F&O algo';
        setError(errorMsg);
        console.error('❌ Error starting F&O algo:', errorMsg);
      }
    } catch (error) {
      const errorMsg = 'Network error: ' + error.message;
      setError(errorMsg);
      console.error('❌ Network error:', error);
    } finally {
      setLoading(false);
    }
  };

  const stopAlgo = async () => {
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/algo/stop`, {
        method: 'POST',
      });

      if (response.ok) {
        setIsRunning(false);
        setStatus(null);
        setSignals([]);
        setPositions({});
      }
    } catch (error) {
      setError('Network error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const squareOffPosition = async (symbol, instrumentType) => {
    try {
      const response = await fetch(`${API_URL}/algo/fno/square-off`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ symbol, instrument_type: instrumentType }),
      });

      if (response.ok) {
        fetchPositions();
      }
    } catch (error) {
      console.error('Error squaring off position:', error);
    }
  };

  const resetDailyMetrics = async () => {
    try {
      const response = await fetch(`${API_URL}/algo/fno/reset-daily`, {
        method: 'POST',
      });

      if (response.ok) {
        fetchStatus();
      }
    } catch (error) {
      console.error('Error resetting daily metrics:', error);
    }
  };

  const getSignalIcon = (signalType) => {
    switch (signalType) {
      case 'BUY':
        return <TrendingUp size={16} color="#10b981" />;
      case 'SELL':
        return <TrendingDown size={16} color="#ef4444" />;
      default:
        return <Activity size={16} color="#6b7280" />;
    }
  };

  const getSignalBadge = (signalType) => {
    switch (signalType) {
      case 'BUY':
        return <span style={{ ...styles.badge, ...styles.badgeSuccess }}>BUY</span>;
      case 'SELL':
        return <span style={{ ...styles.badge, ...styles.badgeError }}>SELL</span>;
      default:
        return <span style={{ ...styles.badge, ...styles.badgeNeutral }}>HOLD</span>;
    }
  };

  const getInstrumentBadge = (instrumentType) => {
    switch (instrumentType) {
      case 'FUTURES':
        return <span style={{ ...styles.badge, ...styles.badgeBlue }}>FUTURES</span>;
      case 'OPTIONS':
        return <span style={{ ...styles.badge, ...styles.badgePurple }}>OPTIONS</span>;
      default:
        return <span style={{ ...styles.badge, ...styles.badgeNeutral }}>UNKNOWN</span>;
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0.00';
    return Number(num).toFixed(2);
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined) return '0.00%';
    return (Number(num) * 100).toFixed(2) + '%';
  };

  return (
    <div style={styles.container}>
      <WorkflowExplanation
        isOpen={showWorkflow}
        onClose={() => setShowWorkflow(false)}
        type="fno"
      />
      {/* Hero Section */}
      <div style={styles.heroSection}>
        <div style={styles.heroContent}>
          <div style={styles.heroTitle}>
            <Zap size={32} />
            <span>F&O Trading with Numerology & Muhurat</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
            <p style={styles.heroSubtitle}>
              Advanced algorithmic trading for Futures & Options with numerology signals and muhurat timing
            </p>
            <button
              onClick={() => setShowWorkflow(true)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '20px',
                padding: '4px 12px',
                color: 'white',
                fontSize: '12px',
                cursor: 'pointer',
                backdropFilter: 'blur(4px)',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
              onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              ℹ️ How it Works?
            </button>
          </div>
        </div>
        <div style={styles.heroActions}>
          {/* Prominent Paper Trading Toggle */}
          <div
            onClick={() => !isRunning && setConfig(prev => ({ ...prev, paper: !prev.paper }))}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: config.paper ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              border: `1px solid ${config.paper ? '#10b981' : '#ef4444'}`,
              borderRadius: '8px',
              padding: '6px 12px',
              cursor: isRunning ? 'not-allowed' : 'pointer',
              opacity: isRunning ? 0.7 : 1,
              transition: 'all 0.2s',
              marginRight: '12px'
            }}
          >
            <div style={{
              width: '36px',
              height: '20px',
              backgroundColor: config.paper ? '#10b981' : '#374151',
              borderRadius: '10px',
              position: 'relative',
              transition: 'all 0.2s'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                backgroundColor: 'white',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: config.paper ? '18px' : '2px',
                transition: 'all 0.2s'
              }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{
                color: config.paper ? '#10b981' : '#ef4444',
                fontWeight: 'bold',
                fontSize: '12px',
                lineHeight: '1.2'
              }}>
                {config.paper ? 'PAPER TRADING' : 'LIVE TRADING'}
              </span>
              <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)' }}>
                {config.paper ? 'Virtual Money' : 'Real Money'}
              </span>
            </div>
          </div>

          {isRunning ? (
            <Button
              onClick={stopAlgo}
              disabled={loading}
              variant="danger"
              icon={<Square size={16} />}
            >
              Stop Algo
            </Button>
          ) : (
            <Button
              onClick={startAlgo}
              disabled={loading}
              variant="secondary"
              icon={<Play size={16} />}
            >
              Start F&O Algo
            </Button>
          )}
          <Button
            onClick={resetDailyMetrics}
            variant="outline"
            style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
            icon={<RotateCcw size={16} />}
          >
            Reset Daily
          </Button>
        </div>
      </div>

      {error && (
        <div style={{ ...styles.signalCard, borderColor: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <AlertTriangle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Custom Tabs */}
      <div style={styles.tabsContainer}>
        {['overview', 'signals', 'positions', 'performance', 'config'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              ...styles.tabButton,
              ...(activeTab === tab ? styles.activeTab : {})
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[6] }}>
          <div style={styles.dashboardGrid}>
            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}>
                  <span style={styles.metricTitle}>Status</span>
                  <Activity size={16} color="var(--text-tertiary)" />
                </div>
                <div style={styles.metricValue}>
                  {isRunning ? <span style={{ color: '#10b981' }}>Running</span> : <span style={{ color: '#6b7280' }}>Stopped</span>}
                </div>
                <div style={styles.metricSubtext}>
                  {status?.uptime_hours ? `${formatNumber(status.uptime_hours)}h uptime` : 'Not running'}
                </div>
              </CardContent>
            </Card>

            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}>
                  <span style={styles.metricTitle}>Daily P&L</span>
                  <TrendingUp size={16} color="var(--text-tertiary)" />
                </div>
                <div style={{ ...styles.metricValue, color: (status?.risk_status?.daily_pnl || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                  ₹{formatNumber(status?.risk_status?.daily_pnl)}
                </div>
                <div style={styles.metricSubtext}>
                  {status?.risk_status?.exposure_percentage ? `${formatNumber(status.risk_status.exposure_percentage)}% exposure` : 'No exposure'}
                </div>
              </CardContent>
            </Card>

            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}>
                  <span style={styles.metricTitle}>Positions</span>
                  <BarChart3 size={16} color="var(--text-tertiary)" />
                </div>
                <div style={styles.metricValue}>
                  {status?.risk_status?.positions?.total || 0}
                </div>
                <div style={styles.metricSubtext}>
                  {status?.risk_status?.positions?.futures || 0} Fut, {status?.risk_status?.positions?.options || 0} Opt
                </div>
              </CardContent>
            </Card>

            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}>
                  <span style={styles.metricTitle}>Win Rate</span>
                  <Target size={16} color="var(--text-tertiary)" />
                </div>
                <div style={styles.metricValue}>
                  {formatPercentage(status?.performance_metrics?.win_rate)}
                </div>
                <div style={styles.metricSubtext}>
                  {status?.performance_metrics?.total_trades || 0} trades
                </div>
              </CardContent>
            </Card>
          </div>

          <div style={styles.grid2}>
            <Card>
              <CardHeader>
                <CardTitle style={styles.sectionTitle}>
                  <Shield size={20} /> Risk Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Capital</span>
                  <span style={styles.infoValue}>₹{formatNumber(status?.risk_status?.capital)}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Available Margin</span>
                  <span style={styles.infoValue}>₹{formatNumber(status?.risk_status?.available_margin)}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Total Exposure</span>
                  <span style={styles.infoValue}>₹{formatNumber(status?.risk_status?.total_exposure)}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Max Drawdown</span>
                  <span style={styles.infoValue}>{formatPercentage(status?.risk_status?.max_drawdown)}</span>
                </div>
                <div style={{ marginTop: theme.spacing[4] }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: theme.spacing[2] }}>
                    <span style={styles.infoLabel}>Daily Loss Limit usage</span>
                    <span style={styles.infoValue}>₹{formatNumber(status?.risk_status?.risk_limits?.max_daily_loss)}</span>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{
                      ...styles.progressFill,
                      width: `${Math.min((Math.abs(status?.risk_status?.daily_pnl || 0) / (status?.risk_status?.risk_limits?.max_daily_loss || 1)) * 100, 100)}%`,
                      backgroundColor: (Math.abs(status?.risk_status?.daily_pnl || 0) > (status?.risk_status?.risk_limits?.max_daily_loss || 1) * 0.8) ? '#ef4444' : '#10b981'
                    }} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle style={styles.sectionTitle}>
                  <Star size={20} /> Numerology & Muhurat
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Numerology Enabled</span>
                  {status?.config?.numerology_enabled ? <CheckCircle size={18} color="#10b981" /> : <XCircle size={18} color="#ef4444" />}
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Muhurat Timing</span>
                  {status?.config?.muhurat_enabled ? <CheckCircle size={18} color="#10b981" /> : <XCircle size={18} color="#ef4444" />}
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Futures Trading</span>
                  {status?.config?.futures_enabled ? <CheckCircle size={18} color="#10b981" /> : <XCircle size={18} color="#ef4444" />}
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Options Trading</span>
                  {status?.config?.options_enabled ? <CheckCircle size={18} color="#10b981" /> : <XCircle size={18} color="#ef4444" />}
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Mode</span>
                  <span style={{ ...styles.badge, ...(status?.config?.paper_trading ? styles.badgeBlue : styles.badgeError) }}>
                    {status?.config?.paper_trading ? 'PAPER' : 'LIVE'}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Real-Time Decision Logs */}
          <Card>
            <CardHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <CardTitle style={styles.sectionTitle}>
                  <Activity size={20} /> Real-Time Decision Logs
                </CardTitle>
                <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                  Updates every 2 seconds
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div style={{
                backgroundColor: '#1e1e1e',
                borderRadius: theme.borderRadius.lg,
                padding: theme.spacing[4],
                fontFamily: 'monospace',
                fontSize: '13px',
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #2d2d2d'
              }}>
                {logs.length === 0 ? (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: theme.spacing[6] }}>
                    <Activity size={32} style={{ opacity: 0.3, marginBottom: '12px' }} />
                    <div>No logs yet. Start the algo to see real-time decision making...</div>
                  </div>
                ) : (
                  logs.map((log, index) => (
                    <div key={index} style={{
                      padding: '8px 0',
                      borderBottom: index < logs.length - 1 ? '1px solid #2d2d2d' : 'none',
                      display: 'flex',
                      gap: '12px',
                      fontSize: '13px'
                    }}>
                      <span style={{ color: '#6b7280', minWidth: '80px' }}>[{log.timestamp}]</span>
                      <span style={{ color: '#4a5568', minWidth: '120px', fontWeight: '600' }}>
                        {log.source}
                      </span>
                      <span style={{
                        color: log.level === 'ERROR' ? '#ef4444' :
                          log.level === 'WARNING' ? '#f59e0b' : '#e5e7eb',
                        fontWeight: log.level === 'ERROR' ? 'bold' : 'normal',
                        flex: 1
                      }}>
                        {log.message}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'signals' && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Trading Signals</CardTitle>
          </CardHeader>
          <CardContent>
            {signals.length === 0 ? (
              <div style={styles.emptyState}>No signals generated yet</div>
            ) : (
              signals.map((signal, index) => (
                <div key={index} style={styles.signalCard}>
                  <div style={styles.signalHeader}>
                    <div style={styles.signalTitle}>
                      {getSignalIcon(signal.signal_type)}
                      <span>{signal.symbol}</span>
                      {getSignalBadge(signal.signal_type)}
                      {getInstrumentBadge(signal.instrument_type)}
                    </div>
                    <span style={styles.infoLabel}>{new Date(signal.timestamp).toLocaleString()}</span>
                  </div>
                  <div style={styles.signalGrid}>
                    <div><span style={styles.signalLabel}>Qty:</span> {signal.quantity}</div>
                    <div><span style={styles.signalLabel}>Compat:</span> {formatPercentage(signal.compatibility_score)}</div>
                    <div><span style={styles.signalLabel}>Conf:</span> {formatPercentage(signal.confidence)}</div>
                    <div><span style={styles.signalLabel}>Muhurat:</span> {formatPercentage(signal.muhurat_score)}</div>
                  </div>
                  <div style={{ marginTop: theme.spacing[2], fontSize: theme.typography.fontSize.sm, color: 'var(--text-secondary)' }}>
                    <div><strong>Numerology:</strong> {signal.numerology_reason}</div>
                    <div><strong>Muhurat:</strong> {signal.muhurat_reason}</div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'positions' && (
        <Card>
          <CardHeader>
            <CardTitle>Current Positions</CardTitle>
          </CardHeader>
          <CardContent>
            {(!positions.futures || Object.keys(positions.futures).length === 0) &&
              (!positions.options || Object.keys(positions.options).length === 0) ? (
              <div style={styles.emptyState}>No open positions</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[4] }}>
                {positions.futures && Object.entries(positions.futures).map(([symbol, position]) => (
                  <div key={symbol} style={styles.signalCard}>
                    <div style={styles.signalHeader}>
                      <div style={styles.signalTitle}>
                        <span>{symbol}</span>
                        <span style={{ ...styles.badge, ...styles.badgeBlue }}>FUTURES</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => squareOffPosition(symbol, 'FUTURES')}>Square Off</Button>
                    </div>
                    <div style={styles.signalGrid}>
                      <div><span style={styles.signalLabel}>Qty:</span> {position.quantity}</div>
                      <div><span style={styles.signalLabel}>Signal:</span> {position.signal_type}</div>
                      <div><span style={styles.signalLabel}>Time:</span> {formatNumber(position.duration_hours)}h</div>
                      <div><span style={styles.signalLabel}>Value:</span> ₹{formatNumber(position.position_value)}</div>
                    </div>
                  </div>
                ))}
                {positions.options && Object.entries(positions.options).map(([symbol, position]) => (
                  <div key={symbol} style={styles.signalCard}>
                    <div style={styles.signalHeader}>
                      <div style={styles.signalTitle}>
                        <span>{symbol}</span>
                        <span style={{ ...styles.badge, ...styles.badgePurple }}>OPTIONS</span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => squareOffPosition(symbol, 'OPTIONS')}>Square Off</Button>
                    </div>
                    <div style={styles.signalGrid}>
                      <div><span style={styles.signalLabel}>Qty:</span> {position.quantity}</div>
                      <div><span style={styles.signalLabel}>Signal:</span> {position.signal_type}</div>
                      <div><span style={styles.signalLabel}>Time:</span> {formatNumber(position.duration_hours)}h</div>
                      <div><span style={styles.signalLabel}>Value:</span> ₹{formatNumber(position.position_value)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'performance' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[6] }}>
          <div style={styles.dashboardGrid}>
            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}><span style={styles.metricTitle}>Total Trades</span><BarChart3 size={16} /></div>
                <div style={styles.metricValue}>{status?.performance_metrics?.total_trades || 0}</div>
                <div style={styles.metricSubtext}>{status?.performance_metrics?.winning_trades || 0} W / {status?.performance_metrics?.losing_trades || 0} L</div>
              </CardContent>
            </Card>
            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}><span style={styles.metricTitle}>Total P&L</span><TrendingUp size={16} /></div>
                <div style={{ ...styles.metricValue, color: (status?.performance_metrics?.total_pnl || 0) >= 0 ? '#10b981' : '#ef4444' }}>₹{formatNumber(status?.performance_metrics?.total_pnl)}</div>
                <div style={styles.metricSubtext}>Futures: ₹{formatNumber(status?.performance_metrics?.futures_pnl)}</div>
              </CardContent>
            </Card>
            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}><span style={styles.metricTitle}>Sharpe Ratio</span><Target size={16} /></div>
                <div style={styles.metricValue}>{formatNumber(status?.performance_metrics?.sharpe_ratio)}</div>
                <div style={styles.metricSubtext}>Risk-adjusted returns</div>
              </CardContent>
            </Card>
            <Card style={styles.metricCard}>
              <CardContent>
                <div style={styles.metricHeader}><span style={styles.metricTitle}>Max Drawdown</span><Shield size={16} /></div>
                <div style={{ ...styles.metricValue, color: '#ef4444' }}>{formatPercentage(status?.performance_metrics?.max_drawdown)}</div>
                <div style={styles.metricSubtext}>Maximum loss from peak</div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'config' && (
        <Card>
          <CardHeader>
            <CardTitle style={styles.sectionTitle}><Settings size={20} /> Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={styles.grid2}>
              <div>
                <h3 style={{ ...styles.sectionTitle, fontSize: theme.typography.fontSize.lg }}>Basic Settings</h3>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>API Key</label>
                  <input type="password" value={config.api_key} onChange={(e) => setConfig({ ...config, api_key: e.target.value })} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Access Token</label>
                  <input type="password" value={config.access_token} onChange={(e) => setConfig({ ...config, access_token: e.target.value })} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Capital (₹)</label>
                  <input type="number" value={config.capital} onChange={(e) => setConfig({ ...config, capital: Number(e.target.value) })} style={styles.input} />
                </div>
              </div>
              <div>
                <h3 style={{ ...styles.sectionTitle, fontSize: theme.typography.fontSize.lg }}>Risk & Limits</h3>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Max Daily Loss (₹)</label>
                  <input type="number" value={config.max_daily_loss} onChange={(e) => setConfig({ ...config, max_daily_loss: Number(e.target.value) })} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Max Futures Pos</label>
                  <input type="number" value={config.max_futures_positions} onChange={(e) => setConfig({ ...config, max_futures_positions: Number(e.target.value) })} style={styles.input} />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Max Options Pos</label>
                  <input type="number" value={config.max_options_positions} onChange={(e) => setConfig({ ...config, max_options_positions: Number(e.target.value) })} style={styles.input} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: theme.spacing[6] }}>
              <h3 style={{ ...styles.sectionTitle, fontSize: theme.typography.fontSize.lg }}>Features & Toggles</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: theme.spacing[4] }}>
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={config.enable_numerology_signals} onChange={(e) => setConfig({ ...config, enable_numerology_signals: e.target.checked })} style={styles.checkbox} />
                  <span>Numerology Signals</span>
                </label>
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={config.enable_muhurat_timing} onChange={(e) => setConfig({ ...config, enable_muhurat_timing: e.target.checked })} style={styles.checkbox} />
                  <span>Muhurat Timing</span>
                </label>
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={config.futures_enabled} onChange={(e) => setConfig({ ...config, futures_enabled: e.target.checked })} style={styles.checkbox} />
                  <span>Futures Trading</span>
                </label>
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={config.options_enabled} onChange={(e) => setConfig({ ...config, options_enabled: e.target.checked })} style={styles.checkbox} />
                  <span>Options Trading</span>
                </label>
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={config.paper} onChange={(e) => setConfig({ ...config, paper: e.target.checked })} style={styles.checkbox} />
                  <span>Paper Trading</span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: theme.spacing[4],
    fontFamily: theme.typography.fontFamily.sans.join(', '),
    backgroundColor: 'var(--bg-secondary)',
    minHeight: '100vh',
  },
  heroSection: {
    background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)',
    borderRadius: theme.borderRadius['2xl'],
    padding: theme.spacing[8],
    marginBottom: theme.spacing[8],
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing[4],
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
  },
  heroTitle: {
    fontSize: theme.typography.fontSize['4xl'],
    fontWeight: theme.typography.fontWeight.bold,
    margin: 0,
    letterSpacing: '-0.025em',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[3],
  },
  heroSubtitle: {
    fontSize: theme.typography.fontSize.lg,
    color: 'white',
    opacity: 0.95,
    margin: '8px 0 0 0',
    fontWeight: theme.typography.fontWeight.medium,
    maxWidth: '600px',
  },
  heroActions: {
    display: 'flex',
    gap: theme.spacing[3],
    zIndex: 1,
  },
  dashboardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: theme.spacing[6],
  },
  metricCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  metricHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing[2],
  },
  metricTitle: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  metricValue: {
    fontSize: theme.typography.fontSize['3xl'],
    fontWeight: theme.typography.fontWeight.bold,
    color: 'var(--text-primary)',
  },
  metricSubtext: {
    fontSize: theme.typography.fontSize.xs,
    color: 'var(--text-tertiary)',
    marginTop: theme.spacing[1],
  },
  tabsContainer: {
    display: 'flex',
    gap: theme.spacing[1],
    backgroundColor: 'var(--bg-tertiary)',
    padding: theme.spacing[1],
    borderRadius: theme.borderRadius.lg,
    marginBottom: theme.spacing[6],
    overflowX: 'auto',
  },
  tabButton: {
    padding: `${theme.spacing[2]} ${theme.spacing[4]}`,
    borderRadius: theme.borderRadius.md,
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontWeight: theme.typography.fontWeight.medium,
    cursor: 'pointer',
    fontSize: theme.typography.fontSize.sm,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease',
  },
  activeTab: {
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    boxShadow: theme.shadows.sm,
  },
  sectionTitle: {
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.bold,
    marginBottom: theme.spacing[4],
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: theme.spacing[6],
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.spacing[3]} 0`,
    borderBottom: '1px solid var(--border-primary)',
  },
  infoLabel: {
    fontSize: theme.typography.fontSize.sm,
    color: 'var(--text-secondary)',
  },
  infoValue: {
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    color: 'var(--text-primary)',
  },
  badge: {
    padding: '2px 8px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  badgeSuccess: {
    backgroundColor: 'rgba(16, 185, 129, 0.1)',
    color: '#10b981',
    border: '1px solid rgba(16, 185, 129, 0.2)',
  },
  badgeError: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    color: '#ef4444',
    border: '1px solid rgba(239, 68, 68, 0.2)',
  },
  badgeWarning: {
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    color: '#f59e0b',
    border: '1px solid rgba(245, 158, 11, 0.2)',
  },
  badgeNeutral: {
    backgroundColor: 'rgba(107, 114, 128, 0.1)',
    color: '#6b7280',
    border: '1px solid rgba(107, 114, 128, 0.2)',
  },
  badgeBlue: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    border: '1px solid rgba(59, 130, 246, 0.2)',
  },
  badgePurple: {
    backgroundColor: 'rgba(139, 92, 246, 0.1)',
    color: '#8b5cf6',
    border: '1px solid rgba(139, 92, 246, 0.2)',
  },
  signalCard: {
    backgroundColor: 'var(--bg-tertiary)',
    padding: theme.spacing[4],
    borderRadius: theme.borderRadius.lg,
    border: '1px solid var(--border-primary)',
    marginBottom: theme.spacing[4],
  },
  signalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing[3],
  },
  signalTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[2],
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.lg,
  },
  signalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: theme.spacing[2],
    fontSize: theme.typography.fontSize.sm,
  },
  signalLabel: {
    color: 'var(--text-secondary)',
    marginRight: theme.spacing[1],
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    color: 'var(--text-tertiary)',
    fontStyle: 'italic',
  },
  inputGroup: {
    marginBottom: theme.spacing[4],
  },
  inputLabel: {
    display: 'block',
    marginBottom: theme.spacing[2],
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    color: 'var(--text-secondary)',
  },
  input: {
    width: '100%',
    padding: theme.spacing[3],
    backgroundColor: 'var(--bg-primary)',
    border: '1px solid var(--border-primary)',
    borderRadius: theme.borderRadius.md,
    color: 'var(--text-primary)',
    fontSize: theme.typography.fontSize.base,
    outline: 'none',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing[3],
    cursor: 'pointer',
    padding: theme.spacing[2],
    borderRadius: theme.borderRadius.md,
    transition: 'background-color 0.2s',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  progressBar: {
    height: '6px',
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s ease, background-color 0.3s ease',
  },
};

export default FnOTradingPage;
